import type { AppName, Chat, Space, Workflow, WorkflowSchedule } from "@rhema/client";
import {
  Button,
  ClockIcon,
  cn,
  focusRing,
  HandIcon,
  PlusIcon,
  ProductMark,
  Select,
  Sheet,
  Switch,
  TextField,
} from "@rhema/ui";
import { type FormEvent, useEffect, useId, useState } from "react";
import { useFailure } from "../billing/UpgradeLink";
import { MARK } from "../links/useLinks";
import { useMenus } from "../menus/actions";
import { MoreButton, WithMenu } from "../menus/WithMenu";
import { useClient, useClientState, useMe } from "../session/session";
import { channelsOf, isForeign } from "./data";

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const REPEATS: { value: WorkflowSchedule["repeat"]; label: string }[] = [
  { value: "daily", label: "Every day" },
  { value: "weekdays", label: "Every weekday" },
  { value: "weekly", label: "Every week" },
];

const APPS: { app: AppName; name: string; detail: string }[] = [
  { app: "queravel", name: "Queravel", detail: "Members can ask Queravel about messages they pick." },
  { app: "malakh", name: "Malakh", detail: "Members can email messages with Malakh." },
  {
    app: "penielly",
    name: "Penielly",
    detail: "Members can share photos from Penielly and save photos there.",
  },
];

/** "09:00" for 540 minutes after midnight. */
export function clock(minute: number): string {
  return `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
}

/** 540 for "09:00", or null when the text is not a time. */
export function minuteOf(text: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : null;
}

/** When a workflow runs, in words: "Every weekday at 09:00" or "When someone joins #general". */
export function scheduleText(
  w: Pick<Workflow, "kind" | "repeat" | "weekday" | "minute" | "tz">,
  channel: string,
) {
  if (w.kind === "welcome") return `When someone joins #${channel}`;
  const time = w.minute === null ? "" : ` at ${clock(w.minute)}`;
  const zone = ` (${w.tz.replace(/_/g, " ")})`;
  if (w.repeat === "weekly") return `Every ${WEEKDAYS[w.weekday ?? 0]}${time}${zone}`;
  return `${w.repeat === "weekdays" ? "Every weekday" : "Every day"}${time}${zone}`;
}

function when(iso: string): string {
  const d = new Date(iso);
  const day = d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  return `${day}, ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
}

function upper(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Who owns a workflow, for its status line. `name` is null when it is the person reading. */
export type Owner = { name: string | null };

/** The state of a workflow in one line. */
export function statusText(w: Workflow, owner: Owner): string {
  const device = owner.name ? `${owner.name}'s device` : "your device";
  if (w.problem)
    return `Stopped. ${upper(owner.name ?? "you")} can no longer post in this channel. Edit the message to start it again.`;
  if (w.paused) return "Paused.";
  if (w.kind === "welcome") {
    if (!w.waiting) return "On.";
    const who = w.waiting === 1 ? "1 person is" : `${w.waiting} people are`;
    return `${who} waiting to be welcomed. It is posted when ${device} is next online.`;
  }
  if (!w.nextRunAt) return "On.";
  const next = `Next on ${when(w.nextRunAt)}.`;
  if (!w.queued) return `${next} Waiting for ${device} to prepare it.`;
  return next;
}

/** Turns Queravel, Malakh and Penielly on or off for every channel in the space. */
export function SpaceApps({ space }: { space: Space }) {
  const client = useClient();
  const { error, fail, clear } = useFailure();
  const [busy, setBusy] = useState<AppName | null>(null);
  const set = async (app: AppName, on: boolean) => {
    setBusy(app);
    clear();
    try {
      await client.updateSpaceApps(space.id, { [app]: on });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };
  return (
    <section aria-labelledby="space-apps" className="flex flex-col gap-2.5">
      <h2 id="space-apps" className="m-0 text-[17px] font-bold">
        Apps
      </h2>
      <p className="m-0 text-sm leading-normal text-ink-secondary">
        These work in every channel of {space.name} unless you turn them off. Each member links their own
        account, and nothing is sent to an app unless they choose it.
      </p>
      {error && (
        <p role="alert" className="m-0 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm text-scarlet">
          {error}
        </p>
      )}
      <ul aria-label="Apps" className="m-0 flex list-none flex-col gap-2 p-0">
        {APPS.map(({ app, name, detail }) => (
          <li
            key={app}
            className="flex items-center gap-3 rounded-card border border-line bg-surface px-4 py-3"
          >
            <ProductMark product={MARK[app]} size={36} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-[15px] font-semibold">{name}</span>
              <span className="text-[12.5px] text-ink-secondary">{detail}</span>
            </span>
            <Switch
              label={`${name} in ${space.name}`}
              checked={space.apps?.[app] !== false}
              disabled={busy === app}
              onChange={(on) => void set(app, on)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The space's workflows, for its admins, with a way to add one. */
export function SpaceWorkflows({ space }: { space: Space }) {
  const client = useClient();
  const state = useClientState();
  const menus = useMenus();
  const list = state.workflows[space.id];
  const [editing, setEditing] = useState<Workflow | "new" | null>(null);
  const { error, fail } = useFailure();

  // biome-ignore lint/correctness/useExhaustiveDependencies: loads once for each space
  useEffect(() => {
    void client.loadWorkflows(space.id).catch((err) => fail(err));
  }, [client, space.id]);

  const channelName = (id: string) => state.chats[id]?.title ?? "a channel";
  const owner = (w: Workflow): Owner => {
    if (w.ownerId === state.me?.id) return { name: null };
    const m = state.chats[w.chatId]?.members.find((x) => x.user.id === w.ownerId);
    return { name: m?.user.display_name ?? "the owner" };
  };

  return (
    <section aria-labelledby="space-workflows" className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-3">
        <h2 id="space-workflows" className="m-0 text-[17px] font-bold">
          Workflows
        </h2>
        <Button size="sm" icon={<PlusIcon size={16} strokeWidth={2.4} />} onClick={() => setEditing("new")}>
          New workflow
        </Button>
      </div>
      <p className="m-0 text-sm leading-normal text-ink-secondary">
        Welcome people who join a channel, or post a message on a schedule, such as a standup question.
        Messages are encrypted on the device of the admin who writes them, and that admin's devices post them.
      </p>
      {error && (
        <p role="alert" className="m-0 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm text-scarlet">
          {error}
        </p>
      )}
      {!list && !error && <p className="m-0 text-sm text-ink-meta">Loading the workflows.</p>}
      {list?.length === 0 && <p className="m-0 text-sm text-ink-meta">No workflows yet.</p>}
      <ul aria-label="Workflows" className="m-0 flex list-none flex-col gap-2.5 p-0">
        {list?.map((w) => {
          const Icon = w.kind === "welcome" ? HandIcon : ClockIcon;
          const items = () => menus.workflow(w, () => setEditing(w));
          const status = statusText(w, owner(w));
          return (
            <WithMenu
              key={w.id}
              label={w.name}
              items={items}
              more={false}
              className="flex gap-3 rounded-card border border-line bg-surface px-4 py-3.5"
            >
              <Icon size={20} className="mt-0.5 flex-none text-ink-secondary" />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex items-center gap-2">
                  <span className="truncate text-[15px] font-bold">{w.name}</span>
                  {(w.paused || w.problem) && (
                    <span className="flex-none rounded-full bg-surface-header px-2 py-0.5 text-[12px] font-semibold text-ink-secondary">
                      {w.problem ? "Stopped" : "Paused"}
                    </span>
                  )}
                </span>
                <span className="text-[13.5px] text-ink-secondary">
                  {scheduleText(w, channelName(w.chatId))}
                </span>
                <span className="mt-1 text-[14px] whitespace-pre-wrap select-text">
                  {w.text ?? (
                    <span className="text-ink-meta italic">
                      This device cannot read the message, because it was not in #{channelName(w.chatId)} when
                      the message was written.
                    </span>
                  )}
                </span>
                <span className={cn("mt-1 text-[12.5px]", w.problem ? "text-scarlet" : "text-ink-meta")}>
                  {status}
                </span>
              </span>
              <MoreButton label={w.name} items={items} className="-mt-1 -mr-2" />
            </WithMenu>
          );
        })}
      </ul>
      {editing && (
        <WorkflowSheet
          space={space}
          workflow={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

/** Makes or edits a workflow. The text is encrypted on this device before it leaves. */
export function WorkflowSheet({
  space,
  workflow,
  onClose,
}: {
  space: Space;
  workflow: Workflow | null;
  onClose: () => void;
}) {
  const client = useClient();
  const state = useClientState();
  const me = useMe();
  const textId = useId();
  // Only channels this admin is in, because the message is encrypted for the channel here, and
  // only ones this space owns, because a shared channel's workflows belong to its own space.
  const channels: Chat[] = channelsOf(state, space.id).filter((c) => !isForeign(c, space.id));
  const zone = workflow?.tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";
  const [kind, setKind] = useState<"welcome" | "recurring">(workflow?.kind ?? "recurring");
  const [chatId, setChatId] = useState(workflow?.chatId ?? channels[0]?.id ?? "");
  const [name, setName] = useState(workflow?.name ?? "");
  const [text, setText] = useState(workflow?.text ?? "");
  const [repeat, setRepeat] = useState<WorkflowSchedule["repeat"]>(workflow?.repeat ?? "weekdays");
  const [weekday, setWeekday] = useState(workflow?.weekday ?? 0);
  const [time, setTime] = useState(clock(workflow?.minute ?? 9 * 60));
  const [busy, setBusy] = useState(false);
  const { error, fail, clear } = useFailure();
  const inChannel = channels.some((c) => c.id === chatId);
  const minute = minuteOf(time);
  const ready = name.trim() && text.trim() && inChannel && (kind === "welcome" || minute !== null);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    clear();
    const schedule: WorkflowSchedule | undefined =
      kind === "recurring" && minute !== null
        ? { repeat, minute, tz: zone, ...(repeat === "weekly" ? { weekday } : {}) }
        : undefined;
    try {
      if (workflow) {
        const sameSchedule =
          !schedule ||
          (workflow.repeat === schedule.repeat &&
            workflow.minute === schedule.minute &&
            (schedule.repeat !== "weekly" || workflow.weekday === schedule.weekday));
        await client.updateWorkflow(space.id, workflow.id, {
          ...(name.trim() !== workflow.name ? { name: name.trim() } : {}),
          ...(text.trim() !== (workflow.text ?? "") ? { text: text.trim() } : {}),
          ...(sameSchedule ? {} : { schedule }),
        });
      } else {
        await client.createWorkflow(space.id, {
          chatId,
          kind,
          name: name.trim(),
          text: text.trim(),
          ...(schedule ? { schedule } : {}),
        });
      }
      onClose();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const channelOptions = channels.map((c) => ({ value: c.id, label: `#${c.title}` }));
  if (workflow && !inChannel)
    channelOptions.push({
      value: workflow.chatId,
      label: `#${state.chats[workflow.chatId]?.title ?? "channel"}`,
    });

  return (
    <Sheet
      open
      onClose={onClose}
      fixed={false}
      title={workflow ? `Edit ${workflow.name}` : "New workflow"}
      className="px-5 pb-7"
    >
      <form onSubmit={(e) => void save(e)} className="mt-3 flex flex-col gap-3.5">
        {!workflow && (
          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="mb-2 p-0 text-[13px] font-semibold text-ink">What it does</legend>
            {(
              [
                [
                  "recurring",
                  "Recurring message",
                  "Posts a message on a schedule, such as a standup question.",
                ],
                ["welcome", "Welcome message", "Welcomes each person who joins the channel."],
              ] as const
            ).map(([value, label, detail]) => (
              <label
                key={value}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-card border bg-surface px-4 py-3",
                  kind === value ? "border-blue" : "border-line",
                )}
              >
                <input
                  type="radio"
                  name="workflow-kind"
                  value={value}
                  checked={kind === value}
                  onChange={() => setKind(value)}
                  className={cn("mt-1 accent-blue", focusRing)}
                />
                <span className="flex flex-col">
                  <span className="text-[15px] font-semibold">{label}</span>
                  <span className="text-[12.5px] text-ink-secondary">{detail}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}
        <Select
          label="Channel"
          value={chatId}
          options={channelOptions}
          disabled={Boolean(workflow)}
          onChange={setChatId}
        />
        {!channels.length && (
          <p className="m-0 text-[13px] text-ink-secondary">
            Join a channel first. Workflows post in channels you are in.
          </p>
        )}
        <TextField
          label="Name"
          placeholder={kind === "welcome" ? "Welcome" : "Daily standup"}
          value={name}
          maxLength={80}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="flex flex-col gap-1.5">
          <label htmlFor={textId} className="text-[13px] font-semibold text-ink">
            Message
          </label>
          <textarea
            id={textId}
            rows={4}
            value={text}
            maxLength={4000}
            placeholder={
              kind === "welcome"
                ? "Welcome! Say hello and tell us what you work on."
                : "What did you do yesterday, and what are you doing today?"
            }
            onChange={(e) => setText(e.target.value)}
            className={cn(
              "rounded-field border border-line bg-surface px-4 py-3 font-sans text-[15px] text-ink focus:border-blue",
              focusRing,
            )}
          />
          {kind === "welcome" && (
            <span className="text-[12.5px] text-ink-secondary">
              It mentions each new person by name, so they get a notification.
            </span>
          )}
        </div>
        {kind === "recurring" && (
          <div className="flex flex-col gap-2.5">
            <div className="flex gap-2.5">
              <Select
                label="Repeats"
                value={repeat}
                options={REPEATS}
                onChange={(v) => setRepeat(v as WorkflowSchedule["repeat"])}
                className="min-w-0 flex-1"
              />
              <TextField
                label="Time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-[130px] flex-none"
              />
            </div>
            {repeat === "weekly" && (
              <Select
                label="Day"
                value={String(weekday)}
                options={WEEKDAYS.map((d, i) => ({ value: String(i), label: d }))}
                onChange={(v) => setWeekday(Number(v))}
              />
            )}
            <span className="text-[12.5px] text-ink-secondary">Times are in {zone.replace(/_/g, " ")}.</span>
          </div>
        )}
        <p className="m-0 text-[12.5px] leading-normal text-ink-secondary">
          {workflow && workflow.ownerId !== me.id && text.trim() !== (workflow.text ?? "")
            ? "Saving new text makes you its owner. Your devices then encrypt and post it."
            : "The message is encrypted on this device for the channel. People who join later can read the messages posted after they join."}
        </p>
        {error && (
          <p role="alert" className="m-0 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm text-scarlet">
            {error}
          </p>
        )}
        <Button type="submit" size="lg" disabled={!ready || busy}>
          {workflow ? "Save changes" : "Create workflow"}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </form>
    </Sheet>
  );
}
